import { useEffect, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import type { Location, Occurrence, OccurrenceOverride } from '../domain/types';
import { formatDayStamp } from '../domain/calendar';
import { formatDuration, formatTime } from '../domain/format';
import { isValidIsoDate } from '../domain/validation';
import { Temporal } from 'temporal-polyfill';
import { useRepository } from './repositoryContext';
import { ErrorBanner, Field } from './components';

export function wallClock(occurrence: Occurrence): string {
  return occurrence.start.toPlainTime().toString().slice(0, 5);
}

/**
 * Actions for one session, as a bottom sheet.
 *
 * The wording carries the distinction people get wrong most often: changing *this
 * class* writes an override for a single occurrence, while *the whole series*
 * edits the recurring rule.
 */
export function OccurrenceSheet(props: {
  occurrence: Occurrence;
  locations: readonly Location[];
  override: OccurrenceOverride | undefined;
  onChanged: () => void;
  onEditSeries: () => void;
  onClose: () => void;
}): JSX.Element {
  const repo = useRepository();
  const { occurrence, locations, override } = props;

  const [moving, setMoving] = useState(false);
  const [newDate, setNewDate] = useState(occurrence.date);
  const [newTime, setNewTime] = useState(wallClock(occurrence));
  const [newLocationId, setNewLocationId] = useState(occurrence.locationId);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);
  const panel = useRef<HTMLElement | null>(null);

  // Move focus into the sheet when it opens. Besides being what a modal should
  // do, it makes Escape work deterministically: a listener on the document only
  // fires if focus happens to be somewhere that bubbles there, which is not
  // guaranteed after a tap.
  useEffect(() => {
    panel.current?.focus();
  }, []);

  const locationName =
    locations.find((location) => location.id === occurrence.locationId)?.name ?? 'Unknown place';

  function report(cause: unknown): void {
    setError(cause instanceof Error ? cause : new Error(String(cause)));
  }

  async function cancelOccurrence(): Promise<void> {
    try {
      await repo.putOverride({
        seriesId: occurrence.seriesId,
        occurrenceKey: occurrence.key,
        originalDate: occurrence.originalDate,
        status: 'cancelled',
        newDate: null,
        newStartTime: null,
        newLocationId: null,
      });
      props.onChanged();
    } catch (cause: unknown) {
      report(cause);
    }
  }

  /** Removing the override is what restores the rule's own answer. */
  async function restore(): Promise<void> {
    try {
      await repo.clearOverride(occurrence.key);
      props.onChanged();
    } catch (cause: unknown) {
      report(cause);
    }
  }

  async function saveMove(event: Event): Promise<void> {
    event.preventDefault();
    if (!isValidIsoDate(newDate)) {
      setFieldError('Pick a real date.');
      return;
    }
    if (!/^([01]\d|2[0-3]):[0-5]\d$/u.test(newTime)) {
      setFieldError('Pick a valid time.');
      return;
    }
    setFieldError(undefined);
    try {
      await repo.putOverride({
        seriesId: occurrence.seriesId,
        occurrenceKey: occurrence.key,
        originalDate: occurrence.originalDate,
        status: 'moved',
        newDate,
        newStartTime: newTime,
        newLocationId,
      });
      setMoving(false);
      props.onChanged();
    } catch (cause: unknown) {
      report(cause);
    }
  }

  return (
    <div class="sheet-backdrop" onClick={props.onClose}>
      <section
        class="sheet"
        role="dialog"
        aria-modal="true"
        aria-label={`${occurrence.title} on ${occurrence.date}`}
        tabIndex={-1}
        ref={(element) => {
          panel.current = element;
        }}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            props.onClose();
          }
        }}
      >
        <div class="sheet-grip" aria-hidden="true" />

        <h3>{occurrence.title}</h3>
        <p class="muted">
          {formatDayStamp(Temporal.PlainDate.from(occurrence.date))} ·{' '}
          {formatTime(wallClock(occurrence))}
        </p>
        <p class="muted">
          {formatDuration(occurrence.durationMinutes)} · {locationName}
        </p>
        {occurrence.originalDate !== occurrence.date ? (
          <p class="muted">
            Originally {formatDayStamp(Temporal.PlainDate.from(occurrence.originalDate))}
          </p>
        ) : null}

        <ErrorBanner error={error} />

        {occurrence.status === 'cancelled' ? <p class="notice">This class is cancelled.</p> : null}
        {occurrence.rescheduled ? <p class="notice">This class was moved.</p> : null}

        {moving ? (
          <form class="stack" onSubmit={(event) => void saveMove(event)}>
            <div class="inline-row">
              <Field label="New date" error={fieldError}>
                <input
                  type="date"
                  value={newDate}
                  onInput={(event) => setNewDate((event.target as HTMLInputElement).value)}
                />
              </Field>
              <Field label="New time">
                <input
                  type="time"
                  value={newTime}
                  onInput={(event) => setNewTime((event.target as HTMLInputElement).value)}
                />
              </Field>
            </div>
            {locations.length > 1 ? (
              <Field label="Place">
                <select
                  value={newLocationId}
                  onChange={(event) => setNewLocationId((event.target as HTMLSelectElement).value)}
                >
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </Field>
            ) : null}
            <button type="submit" class="block primary">
              Save the move
            </button>
            <button type="button" class="block" onClick={() => setMoving(false)}>
              Back
            </button>
          </form>
        ) : (
          <div class="stack">
            <button type="button" class="block" onClick={() => setMoving(true)}>
              Move this class
            </button>
            {occurrence.status === 'scheduled' ? (
              <button type="button" class="block danger" onClick={() => void cancelOccurrence()}>
                Cancel this class
              </button>
            ) : null}
            {override !== undefined ? (
              <button type="button" class="block" onClick={() => void restore()}>
                Reset to schedule
              </button>
            ) : null}
            <button type="button" class="link-row" onClick={props.onEditSeries}>
              Edit the whole series ›
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
